<template>
  <div>
    <v-row class="mb-4">
      <v-col>
        <h3 class="text-h6 font-weight-medium mb-4">
          Permissions & Access
        </h3>
        <p class="text--secondary">
          View your account permissions and access levels.
        </p>
      </v-col>
    </v-row>

    <v-card outlined>
      <v-card-text>
        <!-- Role Information -->
        <v-row class="mb-3">
          <v-col>
            <div class="text-subtitle-2 font-weight-bold mb-2">
              Role
            </div>
            <v-chip
              :color="currentUser.access.role === 'internal' ? 'blue' : 'grey'"
              :text-color="currentUser.access.role === 'internal' ? 'white' : 'white'"
              small
            >
              {{ currentUser.access.role === 'internal' ? 'Internal' : 'External' }}
            </v-chip>
          </v-col>
        </v-row>

        <!-- Access Level -->
        <v-row class="mb-3">
          <v-col>
            <div class="text-subtitle-2 font-weight-bold mb-2">
              Access Level
            </div>
            <div class="text-body-2">
              {{ currentUser.access.level }}
            </div>
          </v-col>
        </v-row>

        <!-- Custom Roles -->
        <v-row>
          <v-col>
            <div class="text-subtitle-2 font-weight-bold mb-2">
              Custom Roles
            </div>
            <div v-if="hasCustomRoles">
              <v-chip
                v-if="currentUser.hasCustomRoleAdmin"
                color="red"
                text-color="white"
                small
                class="mr-2 mb-2"
              >
                <font-awesome-icon
                  :icon="['fas', 'shield-halved']"
                  class="mr-1"
                />
                Admin
              </v-chip>
              <v-chip
                v-if="currentUser.hasCustomRoleDev"
                color="purple"
                text-color="white"
                small
                class="mr-2 mb-2"
              >
                <font-awesome-icon
                  :icon="['fas', 'code']"
                  class="mr-1"
                />
                Developer
              </v-chip>
            </div>
            <div
              v-else
              class="text-body-2 text--secondary"
            >
              No custom roles assigned
            </div>
          </v-col>
        </v-row>

        <!-- Update Password Section (if user has permission) -->
        <v-divider
          v-if="currentUser.hasAccessToUpdatePassword"
          class="my-4"
        />

        <v-row v-if="currentUser.hasAccessToUpdatePassword">
          <v-col>
            <div class="text-subtitle-2 font-weight-bold mb-2">
              Account Security
            </div>
            <v-btn
              color="primary"
              outlined
              class="text-none"
              @click="promptToUpdatePassword"
            >
              <font-awesome-icon
                :icon="['fas', 'lock']"
                size="sm"
                class="mr-2"
              />
              Update Password
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Update Password Dialog -->
    <v-dialog
      v-model="showViewToUpdatePassword"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          <span class="text-h6 font-weight-bold">Update Password</span>
          <v-spacer />
          <v-btn
            icon
            @click="cancelUpdatePassword"
          >
            <font-awesome-icon :icon="['fas', 'xmark']" />
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-form
            ref="passwordForm"
            v-model="validPassword"
          >
            <v-text-field
              v-model="inputPassword"
              label="New Password"
              type="password"
              outlined
              :rules="passwordRules"
              required
              @keydown.enter="updateAuthPassword"
            />
          </v-form>

          <v-alert
            v-if="errorUpdatingPassword"
            type="error"
            dense
            class="mt-3"
          >
            {{ errorUpdatingPassword }}
          </v-alert>

          <v-alert
            v-if="successUpdatingPassword"
            type="success"
            dense
            class="mt-3"
          >
            Password updated successfully!
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="cancelUpdatePassword"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!validPassword || !inputPassword"
            :loading="updatingPassword"
            @click="updateAuthPassword"
          >
            Update Password
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapGetters: mapAuthGetters, mapActions: mapAuthActions, mapState: mapAuthState } = createNamespacedHelpers('auth')

export default {
  name: 'SettingsPermissions',
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      inputPassword: '',
      errorUpdatingPassword: '',
      successUpdatingPassword: false,
      showViewToUpdatePassword: false,
      updatingPassword: false,
      validPassword: false,
      passwordRules: [
        (v) => !!v || 'Password is required',
        (v) => (v && v.length >= 8) || 'Password must be at least 8 characters',
        (v) => /(?=.*[a-z])/.test(v) || 'Password must contain at least one lowercase letter',
        (v) => /(?=.*[A-Z])/.test(v) || 'Password must contain at least one uppercase letter',
        (v) => /(?=.*\d)/.test(v) || 'Password must contain at least one number',
      ],
    }
  },
  computed: {
    ...mapAuthGetters([
      'currentUser',
      'isUserInternalDev',
    ]),
    ...mapAuthState(['user']),

    hasCustomRoles() {
      return this.currentUser.hasCustomRoleAdmin || this.currentUser.hasCustomRoleDev
    },

    userEmail() {
      return (this.user && this.user.email) || ''
    },
  },
  methods: {
    ...mapAuthActions([
      'updatePassword',
    ]),

    promptToUpdatePassword() {
      this.showViewToUpdatePassword = true
      this.errorUpdatingPassword = ''
      this.successUpdatingPassword = false
    },

    cancelUpdatePassword() {
      this.showViewToUpdatePassword = false
      this.inputPassword = ''
      this.errorUpdatingPassword = ''
      this.successUpdatingPassword = false
    },

    handleUpdatePasswordError(message) {
      this.errorUpdatingPassword = message
      this.$store.dispatch('messages/showError', message, { root: true })
    },

    async updateAuthPassword() {
      if (!this.validPassword) return

      this.updatingPassword = true
      this.errorUpdatingPassword = ''
      this.successUpdatingPassword = false

      try {
        const domainName = this.userEmail.split('@')[1]

        const domainDoc = await this.$store.dispatch('auth/getDomainDocFromFirestore', domainName)

        if (!domainDoc || domainDoc.error) {
          const defaultError = { code: '403', layer: 'app', message: 'Domain Error' }
          const error = domainDoc?.error || defaultError
          this.handleUpdatePasswordError(error.message)
          return
        }

        if (!domainDoc.isActive) {
          this.handleUpdatePasswordError('Domain Inactive')
          return
        }

        const updatePasswordData = {
          domainDoc,
          email: this.userEmail,
          password: this.inputPassword,
        }

        const result = await this.$store.dispatch('auth/updatePassword', updatePasswordData)

        if (!result) {
          this.handleUpdatePasswordError('Error Updating Password')
          return
        }

        if (result.error) {
          this.handleUpdatePasswordError(result.message)
          return
        }

        this.successUpdatingPassword = true
        this.inputPassword = ''

        setTimeout(() => {
          this.cancelUpdatePassword()
        }, 2000)
      } catch (error) {
        const messageMap = {
          'auth/requires-recent-login': 'Recent Login Required',
        }

        const { code, message: givenMessage } = error || {}
        const rawMessage = givenMessage || 'Update Password Error'
        const message = messageMap[code] || rawMessage.replace('Firebase: ', 'Auth ')

        this.handleUpdatePasswordError(message)
      } finally {
        this.updatingPassword = false
      }
    },
  },
}
</script>
